import { useEffect, useRef, useState } from 'preact/hooks';
import { useAuth } from '../../context/AuthContext.js';
import { hasSent, markSent, rememberClosed, shouldShowPopup } from '../../home/homePopupStore.js';
import { useFetch } from '../../hooks/useFetch.js';
import { api } from '../../services/api.js';
import { setPromptSlotBusy } from '../../shell/promptSlot.js';

/**
 * 389 (#1305, mock-up v2 screens 1–3) — the home pop-up: the posters staff published, over the home page, as often as
 * staff chose (395: every visit, once a day or once per change; closing always hides it for the current visit). The platform decides what is live (`GET /home-popup`); this component decides only whether this
 * device should see it now.
 */

interface Poster {
  id: string;
  image_url: string;
  button_label: string | null;
  link: string | null;
}

interface PopupRead {
  version: string | null;
  posters: Poster[];
  /** 395: how often staff want it shown; `shouldShowPopup` reads anything else as every visit. */
  frequency?: string;
}

/** FR-007: the pictures must load within this long of the home page, or the pop-up waits for the next visit. */
export const HOME_POPUP_PICTURE_DEADLINE_MS = 4000;
const SWIPE_PX = 40;

function openLink(link: string): void {
  window.open(link, '_blank', 'noopener,noreferrer');
}

function sendCount(version: string, kind: 'shown' | 'seen' | 'tap', posterId?: string): void {
  const key = posterId ? `${kind}:${posterId}` : kind;
  if (hasSent(version, key)) return;
  markSent(version, key);
  // Counts are best effort: a failure is never shown and never retried.
  void api
    .post('/home-popup/events', {
      version,
      events: [posterId ? { kind, poster_id: posterId } : { kind }],
    })
    .catch(() => {});
}

export function HomePopup() {
  const { isImpersonating } = useAuth();
  const read = useFetch<PopupRead>(isImpersonating ? null : '/home-popup');
  const data = read.status === 'ok' ? read.data : null;
  const version = data?.version ?? null;
  const candidates =
    version && shouldShowPopup(data?.frequency, version) ? (data?.posters ?? []) : [];

  // The posters whose pictures loaded, decided once: when every picture settled, or at the deadline.
  const [shown, setShown] = useState<Poster[] | null>(null);
  const [index, setIndex] = useState(0);
  const [open, setOpen] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);
  const returnFocus = useRef<Element | null>(null);
  const swipeFrom = useRef<number | null>(null);

  const candidateKey = candidates.map((p) => p.image_url).join('|');
  // biome-ignore lint/correctness/useExhaustiveDependencies: candidateKey stands for the candidates list
  useEffect(() => {
    if (!version || candidates.length === 0) return;
    let decided = false;
    const status = new Map<string, 'loaded' | 'failed'>();
    const decide = () => {
      if (decided) return;
      decided = true;
      const loaded = candidates.filter((p) => status.get(p.image_url) === 'loaded');
      if (loaded.length > 0) setShown(loaded);
    };
    for (const p of candidates) {
      const img = new Image();
      img.onload = () => {
        status.set(p.image_url, 'loaded');
        if (status.size === candidates.length) decide();
      };
      img.onerror = () => {
        status.set(p.image_url, 'failed');
        if (status.size === candidates.length) decide();
      };
      img.src = p.image_url;
    }
    const timer = setTimeout(decide, HOME_POPUP_PICTURE_DEADLINE_MS);
    return () => {
      decided = true;
      clearTimeout(timer);
    };
  }, [version, candidateKey]);

  // Open once there is something loaded to show.
  useEffect(() => {
    if (!shown || shown.length === 0 || !version) return;
    returnFocus.current = document.activeElement;
    setIndex(0);
    setOpen(true);
    sendCount(version, 'shown');
  }, [shown, version]);

  const current = open && shown ? shown[index] : undefined;

  useEffect(() => {
    if (!open) return;
    setPromptSlotBusy(true);
    closeRef.current?.focus();
    return () => setPromptSlotBusy(false);
  }, [open]);

  useEffect(() => {
    if (current && version) sendCount(version, 'seen', current.id);
  }, [current, version]);

  function close() {
    if (version) rememberClosed(version);
    setOpen(false);
    setShown(null);
    const back = returnFocus.current as HTMLElement | null;
    if (back && typeof back.focus === 'function') back.focus();
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: close reads the current version only
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (!current || !shown || !version) return null;
  const count = shown.length;

  function use(poster: Poster) {
    if (!poster.link || !version) return;
    sendCount(version, 'tap', poster.id);
    openLink(poster.link);
    close();
  }

  const go = (to: number) => setIndex(Math.max(0, Math.min(count - 1, to)));

  // z-[999998]: one above the third-party contact button (z 999997, measured in the browser), so while the pop-up is
  // open nothing sits on top of it.
  // The backdrop's click is the pointer twin of Escape (handled on the document above) and of the Close button, so the
  // keyboard already has both. A native <dialog> is not used: its top layer would sit above the app's own banners
  // and its focus rules differ by browser; this one moves focus to Close and back explicitly.
  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: Escape and Close are the keyboard routes (see above)
    <div
      data-testid="home-popup-backdrop"
      class="fixed inset-0 z-[999998] flex items-center justify-center bg-black/55 px-7"
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      {/* biome-ignore lint/a11y/useSemanticElements: an explicit dialog role, not <dialog> (see above) */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={current.button_label ?? 'CARii announcement'}
        class="relative flex w-full max-w-[330px] flex-col items-center gap-3.5"
      >
        <div
          data-testid="home-popup-frame"
          class="relative aspect-[4/5] w-full touch-pan-y select-none"
          onPointerDown={(e) => {
            swipeFrom.current = e.clientX;
          }}
          onPointerUp={(e) => {
            const from = swipeFrom.current;
            swipeFrom.current = null;
            if (from === null) return;
            const dx = e.clientX - from;
            if (dx <= -SWIPE_PX) go(index + 1);
            else if (dx >= SWIPE_PX) go(index - 1);
          }}
        >
          {/* biome-ignore lint/a11y/useKeyWithClickEvents: the picture's link is also the poster's button when one is set; a picture without a button is a convenience for pointer users only */}
          <img
            data-testid="home-popup-picture"
            src={current.image_url}
            alt={current.button_label ?? 'CARii announcement'}
            class={`h-full w-full rounded-[18px] bg-surface object-contain shadow-[0_8px_24px_rgba(0,0,0,0.5)] ${current.link ? 'cursor-pointer' : ''}`}
            draggable={false}
            onClick={() => use(current)}
          />
          {index > 0 && (
            <button
              type="button"
              aria-label="Previous poster"
              onClick={() => go(index - 1)}
              class="absolute top-[40%] -left-5 flex h-11 w-11 items-center justify-center rounded-full bg-bg/70 text-xl text-text"
            >
              ‹
            </button>
          )}
          {index < count - 1 && (
            <button
              type="button"
              aria-label="Next poster"
              onClick={() => go(index + 1)}
              class="absolute top-[40%] -right-5 flex h-11 w-11 items-center justify-center rounded-full bg-bg/70 text-xl text-text"
            >
              ›
            </button>
          )}
        </div>
        <button
          ref={closeRef}
          type="button"
          aria-label="Close"
          onClick={close}
          class="absolute -top-3.5 -right-3.5 flex h-11 w-11 items-center justify-center rounded-full bg-white text-2xl font-bold text-bg shadow-md"
        >
          ×
        </button>
        <div data-testid="home-popup-button-slot" class="flex h-[52px] w-full items-center">
          {current.button_label && current.link && (
            <button
              type="button"
              onClick={() => use(current)}
              class="w-full rounded-full bg-primary px-4 py-3.5 text-[15px] font-bold text-on-primary"
            >
              {`${current.button_label} ↗`}
            </button>
          )}
        </div>
        {count > 1 && (
          <div class="flex justify-center gap-2">
            {shown.map((p, i) => (
              <span
                key={p.id}
                data-testid="home-popup-dot"
                aria-current={i === index ? 'true' : undefined}
                class={
                  i === index
                    ? 'h-2 w-[22px] rounded-full bg-primary'
                    : 'h-2 w-2 rounded-full bg-text-muted'
                }
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
